import { useState } from 'react';
import { doc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

export default function DemoResetModal({ demo, onClose, onSuccess }) {
  const { currentAgent } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleReset = async () => {
    if (!demo) {
      setError('No demo data available');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const demoRef = doc(db, 'demo_bookings', demo.id);
      const now = new Date();
      
      // Get current agent info from auth context
      const currentAgentCode = currentAgent?.agent_id || currentAgent?.id || 'unknown';
      const currentAgentName = currentAgent?.name || 'Unknown Agent';
      
      // Store old values before reset
      const oldOutcome = demo.payload?.outcome || 'unknown';
      const oldStatus = demo.payload?.status || 'unknown';

      // Prepare reset data - delete both outcome and status fields entirely
      const resetData = {
        'payload.outcome': deleteField(),
        'payload.status': deleteField(),
        'payload.reset': {
          date: now.toISOString().split('T')[0], // YYYY-MM-DD format
          time: now.toISOString(), // timestamp
          reset_by_code: currentAgentCode,
          reset_by_name: currentAgentName,
          old_outcome: oldOutcome,
          old_status: oldStatus
        },
        'payload.updatedAt': now,
        'payload.resetAt': now
      };

      await updateDoc(demoRef, resetData);

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error resetting demo:', err);
      setError('Failed to reset demo. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
          <h2 className="text-lg font-semibold text-gray-900">Reset Demo Status</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* Demo Info */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Demo Details</h3>
            <div className="space-y-1 text-sm">
              <p><span className="font-medium">Customer:</span> {demo?.payload?.customer?.name || 'N/A'}</p>
              <p><span className="font-medium">Agent:</span> {demo?.payload?.agentCode || 'N/A'}</p>
              <p><span className="font-medium">Current Status:</span> {demo?.payload?.status || 'No status'}</p>
              <p><span className="font-medium">Current Outcome:</span> {demo?.payload?.outcome || 'No outcome'}</p>
            </div>
          </div>

          {/* Warning */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-yellow-800">Reset Confirmation</p>
                <p className="text-sm text-yellow-700 mt-1">
                  This will remove the outcome and status fields, making the demo similar to a virgin record. The original values will be stored in the reset history.
                </p>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleReset}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Resetting...' : 'Reset Demo'}
          </button>
        </div>
      </div>
    </div>
  );
}
